import {
	Component,
	ElementRef,
	effect,
	input,
	InputSignal,
	QueryList,
	signal,
	ViewChildren,
	WritableSignal,
	Output,
	EventEmitter
} from '@angular/core';
import { SHARED_MODULES } from '../../../shared.';
import { PhIconComponent } from '../ph-icon/ph-icon.component';
import { PhIconWrapperComponent } from '../ph-icon-wrapper/ph-icon-wrapper.component';
import { UserLogoGenerateComponent } from '../user-logo-generate/user-logo-generate.component';
import { IMentionableUsers, StoreItem } from '../../models/chat';

@Component({
	selector: 'ph-chat-mention-picker',
	templateUrl: './ph-chat-mention-picker.component.html',
	styleUrls: ['./ph-chat-mention-picker.component.scss'],
	imports: [
		...SHARED_MODULES,
		PhIconComponent,
		PhIconWrapperComponent,
		UserLogoGenerateComponent
	]
})
export class PhChatMentionPickerComponent {

	public filteredUsers: InputSignal<Array<IMentionableUsers>> = input<Array<IMentionableUsers>>([]);
	public filteredStores: InputSignal<Array<StoreItem>> = input<Array<StoreItem>>([]);
	public allStores: InputSignal<Array<StoreItem>> = input<Array<StoreItem>>([]);

	public selectedTabIndex: WritableSignal<number> = signal<number>(0);
	public expandedStoreId: WritableSignal<number | null> = signal<number | null>(null);
	public selectedMentionIndex: WritableSignal<number> = signal<number>(-1);

	@ViewChildren('pickerContainer') pickerContainers!: QueryList<ElementRef<HTMLDivElement>>;
	@ViewChildren('userItem') userItems!: QueryList<ElementRef<HTMLDivElement>>;
	@ViewChildren('storeItem') storeItems!: QueryList<ElementRef<HTMLDivElement>>;

	@Output() public userSelected: EventEmitter<IMentionableUsers> = new EventEmitter<IMentionableUsers>();
	@Output() public storeSelected: EventEmitter<StoreItem> = new EventEmitter<StoreItem>();
	@Output() public pickerClosed: EventEmitter<void> = new EventEmitter<void>();

	constructor() {
		effect((): void => {
			const hasStores: boolean = this.allStores().length > 0;
			const hasUsers: boolean = this.filteredUsers().length > 0;
			const hasGroups: boolean = this.filteredStores().length > 0;
			const currentTab: number = this.selectedTabIndex();

			if (!hasStores) return;

			if (currentTab === 0 && !hasUsers && hasGroups) {
				this.selectedTabIndex.set(1);
			} else if (currentTab === 1 && !hasGroups && hasUsers) {
				this.selectedTabIndex.set(0);
			}
		});
	}

	public selectedTabChanged(index: number): void {
		this.selectedTabIndex.set(index);
		this.selectedMentionIndex.set(-1);
		this.expandedStoreId.set(null);
	}

	public toggleStoreExpand(storeId: number): void {
		this.expandedStoreId.set(this.expandedStoreId() === storeId ? null : storeId);
	}

	public handleKeyDown(event: KeyboardEvent): boolean {
		const users: Array<IMentionableUsers> = this.filteredUsers() || [];
		const stores: Array<StoreItem> = this.filteredStores() || [];
		const activeList: Array<IMentionableUsers> | Array<StoreItem> = this.selectedTabIndex() === 0 ? users : stores;
		const hasItems: boolean = !!activeList.length;

		if (!hasItems) {
			return false;
		}

		if (event.key === 'ArrowDown') {
			event.preventDefault();
			const nextIndex: number = Math.min(this.selectedMentionIndex() + 1, activeList.length - 1);
			this.selectedMentionIndex.set(nextIndex);
			this.scrollSelectedIntoView();
			return true;
		}

		if (event.key === 'ArrowUp') {
			event.preventDefault();
			const prevIndex: number = Math.max(this.selectedMentionIndex() - 1, 0);
			this.selectedMentionIndex.set(prevIndex);
			this.scrollSelectedIntoView();
			return true;
		}

		if (event.key === 'Tab') {
			event.preventDefault();
			const selectedItem: IMentionableUsers | StoreItem = activeList[this.selectedMentionIndex()];
			if (selectedItem) {
				if (this.selectedTabIndex() === 0) {
					this.userSelected.emit(selectedItem as IMentionableUsers);
				} else {
					this.storeSelected.emit(selectedItem as StoreItem);
				}
			}
			return true;
		}

		if (event.key === 'Enter') {
			const selectedItem: IMentionableUsers | StoreItem = activeList[this.selectedMentionIndex()];
			if (selectedItem) {
				event.preventDefault();
				if (this.selectedTabIndex() === 0) {
					this.userSelected.emit(selectedItem as IMentionableUsers);
				} else {
					this.storeSelected.emit(selectedItem as StoreItem);
				}
				return true;
			}
			return false;
		}

		if (event.key === 'Escape') {
			event.preventDefault();
			event.stopPropagation();
			this.selectedMentionIndex.set(-1);
			this.pickerClosed.emit();
			return true;
		}

		return false;
	}

	private scrollSelectedIntoView(): void {
		setTimeout((): void => {
			const container: HTMLDivElement = this.pickerContainers.toArray()[this.selectedTabIndex()]?.nativeElement;
			const items: QueryList<ElementRef<HTMLDivElement>> = this.selectedTabIndex() === 0
				? this.userItems
				: this.storeItems;
			const selectedItem: HTMLDivElement = items.toArray()[this.selectedMentionIndex()]?.nativeElement;

			if (container && selectedItem) {
				const offset: number = 4;
				const containerTop: number = container.scrollTop;
				const containerBottom: number = containerTop + container.clientHeight;
				const itemTop: number = selectedItem.offsetTop - offset;
				const itemBottom: number = selectedItem.offsetTop + selectedItem.clientHeight + offset;

				if (itemTop < containerTop) {
					container.scrollTop = itemTop;
				} else if (itemBottom > containerBottom) {
					container.scrollTop = itemBottom - container.clientHeight;
				}
			}
		});
	}
}

